import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import { Ds2ExperimentProvider } from 'src/app/shared/hooks/ds2Experiment';
import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type {
	DecisionMakerProfilePageCombinedState,
	DecisionMakerProfilePagePrefetchedContext,
	DecisionMakerProfilePagePrefetchedUserContext,
} from './hook';
import { usePageContext } from './hook';
import { DecisionMakerProfilePageDataContextProvider } from './pageData';
import { DecisionMakerProfilePageFcmContextProvider } from './pageFcm';
import { DecisionMakerProfilePageFcmExperimentsContextProvider } from './pageFcmExperiments';
import { DMProfilePagePaginatedResponsesDataContextProvider } from './pageResponsesPaginatedData';
import { DMProfilePagePaginatedTargetedPetitionsDataContextProvider } from './pageTargetedPetitionsPaginatedData';

export type ErrorHandlerProps = { context: ErrorStateDetails<DecisionMakerProfilePageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: DecisionMakerProfilePagePrefetchedContext | undefined;
	prefectedUserData: DecisionMakerProfilePagePrefetchedUserContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function DecisionMakerProfilePageContextProvider({
	children,
	slug,
	prefetchedData,
	prefectedUserData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(slug, prefetchedData, prefectedUserData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const {
		ds2ExperimentData,
		pageData,
		pageFcm,
		pageFcmExperiments,
		responsesPaginatedData,
		targetedPetitionsPaginatedData,
	} = pageContextState;

	return (
		<DecisionMakerProfilePageDataContextProvider pageData={pageData}>
			<DMProfilePagePaginatedResponsesDataContextProvider responsesPaginatedData={responsesPaginatedData}>
				<DMProfilePagePaginatedTargetedPetitionsDataContextProvider
					targetedPetitionsPaginatedData={targetedPetitionsPaginatedData}
				>
					<DecisionMakerProfilePageFcmContextProvider pageFcm={pageFcm}>
						<DecisionMakerProfilePageFcmExperimentsContextProvider pageFcmExperiments={pageFcmExperiments}>
							<Ds2ExperimentProvider value={ds2ExperimentData}>
								<TrackingContext properties={{ decision_maker_id: pageData.id }}>{children}</TrackingContext>
							</Ds2ExperimentProvider>
						</DecisionMakerProfilePageFcmExperimentsContextProvider>
					</DecisionMakerProfilePageFcmContextProvider>
				</DMProfilePagePaginatedTargetedPetitionsDataContextProvider>
			</DMProfilePagePaginatedResponsesDataContextProvider>
		</DecisionMakerProfilePageDataContextProvider>
	);
}
